import type { LoremSourceName } from "./types";

// Per-document lorem settings per Engineering brief §C7.
// Persistence is per-document (doc metadata in production, localStorage
// in this iteration). New docs inherit the user's last-used settings —
// see `getUserDefaults` / `setUserDefaults` for the workspace fallback.

export type LoremCount = 1 | 3 | 5;

export interface LoremDocSettings {
	count: LoremCount;
	source: LoremSourceName;
}

export const DEFAULT_LOREM_SETTINGS: LoremDocSettings = {
	count: 3,
	source: "lorem",
};

const DOC_KEY_PREFIX = "lilia.lorem.doc.";
const USER_DEFAULTS_KEY = "lilia.lorem.user.v1";

// Per Q1.5 (locked decision): defaults aren't materialized in storage
// until the first lorem trigger. `readDocSettings` therefore returns
// null when nothing has been persisted, and `materializeDefaults` is
// the explicit hook the trigger calls.

function isValidSettings(v: unknown): v is LoremDocSettings {
	if (!v || typeof v !== "object") return false;
	const o = v as { count?: unknown; source?: unknown };
	return (
		(o.count === 1 || o.count === 3 || o.count === 5) &&
		(o.source === "lorem" ||
			o.source === "lorem-tech" ||
			o.source === "lorem-math")
	);
}

export function readDocSettings(docId: string): LoremDocSettings | null {
	if (typeof window === "undefined") return null;
	try {
		const raw = window.localStorage.getItem(DOC_KEY_PREFIX + docId);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		return isValidSettings(parsed) ? parsed : null;
	} catch {
		return null;
	}
}

export function writeDocSettings(
	docId: string,
	settings: LoremDocSettings,
): void {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.setItem(
			DOC_KEY_PREFIX + docId,
			JSON.stringify(settings),
		);
	} catch {
		// Storage may be unavailable (private mode, quota); skip silently.
	}
}

export function getUserDefaults(): LoremDocSettings {
	if (typeof window === "undefined") return DEFAULT_LOREM_SETTINGS;
	try {
		const raw = window.localStorage.getItem(USER_DEFAULTS_KEY);
		if (!raw) return DEFAULT_LOREM_SETTINGS;
		const parsed = JSON.parse(raw);
		return isValidSettings(parsed) ? parsed : DEFAULT_LOREM_SETTINGS;
	} catch {
		return DEFAULT_LOREM_SETTINGS;
	}
}

export function setUserDefaults(settings: LoremDocSettings): void {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.setItem(USER_DEFAULTS_KEY, JSON.stringify(settings));
	} catch {
		// noop
	}
}

/**
 * Returns the effective settings for a document — persisted value if
 * present, else the user's last-used defaults. Does NOT write to
 * storage; that happens via `materializeDefaults` on first lorem
 * trigger (Q1.5 lazy migration).
 */
export function effectiveDocSettings(docId: string): LoremDocSettings {
	const persisted = readDocSettings(docId);
	if (persisted) return persisted;
	return getUserDefaults();
}

/**
 * Lazy migration entry-point per Q1.5 — call from the lorem trigger
 * the first time the user invokes lorem on a doc. If settings already
 * exist on the doc, this is a no-op. Otherwise it writes the user's
 * last-used defaults onto the doc and returns them.
 */
export function materializeDefaults(docId: string): LoremDocSettings {
	const existing = readDocSettings(docId);
	if (existing) return existing;
	const defaults = getUserDefaults();
	writeDocSettings(docId, defaults);
	return defaults;
}

/**
 * Update doc settings AND user defaults atomically — what the
 * settings UI calls when the user picks a new count/source. Keeping
 * them in sync means the next new doc inherits this user's preference.
 */
export function updateDocSettings(
	docId: string,
	patch: Partial<LoremDocSettings>,
): LoremDocSettings {
	const current = effectiveDocSettings(docId);
	const next: LoremDocSettings = { ...current, ...patch };
	writeDocSettings(docId, next);
	setUserDefaults(next);
	return next;
}
